import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { FiSearch, FiFilter } from 'react-icons/fi';
import { GiCoral } from 'react-icons/gi';
import { Input, Select, Button, WaveBackground } from '../styles/GlobalStyles';
import ItemCard from '../components/ItemCard';
import ProductModal from '../components/ProductModal';

const PageHeader = styled(WaveBackground)`
  background: ${props => props.theme.gradients.coral};
  color: white;
  padding: 80px 0 60px;
  text-align: center;
`;

const HeaderContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 2.5rem;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const PageDescription = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
  line-height: 1.6;
`;

const ContentSection = styled.section`
  padding: 80px 0;
  background: ${props => props.theme.colors.background};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const FiltersSection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: ${props => props.theme.borderRadius.large};
  box-shadow: ${props => props.theme.shadows.card};
  margin-bottom: 3rem;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr auto;
  gap: 1.5rem;
  align-items: end;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FilterLabel = styled.label`
  font-weight: 500;
  color: ${props => props.theme.colors.primary};
  font-size: 0.9rem;
`;

const SearchInput = styled(Input)`
  position: relative;
  padding-left: 3rem;
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.grayLight};
  pointer-events: none;
`;

const SearchContainer = styled.div`
  position: relative;
`;

const ResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const ResultsCount = styled.h2`
  color: ${props => props.theme.colors.primary};
  font-size: 1.5rem;
  font-weight: 600;
`;

const SortSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${props => props.theme.colors.textLight};
  font-size: 1.1rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  
  .empty-icon {
    font-size: 4rem;
    color: ${props => props.theme.colors.grayLight};
    margin-bottom: 1rem;
  }
  
  .empty-title {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 0.5rem;
  }
  
  .empty-description {
    color: ${props => props.theme.colors.textLight};
    margin-bottom: 2rem;
  }
`;

const Corals = () => {
  const [corals, setCorals] = useState([]);
  const [filteredCorals, setFilteredCorals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [colorFilter, setColorFilter] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const navigate = useNavigate();

  const fetchCorals = async (retryCount = 0) => {
    try {
      setLoading(true);
      const response = await axios.get('/api/corals');
      setCorals(response.data);
    } catch (error) {
      console.error('Error fetching corals:', error);
      
      // Retry logic for Railway cold starts
      if (retryCount < 2 && (error.code === 'ECONNABORTED' || error.message === 'Request aborted')) {
        setTimeout(() => fetchCorals(retryCount + 1), 2000);
        return;
      }
    } finally {
      if (retryCount === 0) {
        setLoading(false);
      }
    }
  };

  const filterAndSortCorals = React.useCallback(() => {
    let filtered = [...corals];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(coral =>
        coral.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        coral.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Difficulty filter
    if (difficultyFilter) {
      filtered = filtered.filter(coral => coral.difficulty_level === difficultyFilter);
    }

    // Color filter
    if (colorFilter) {
      filtered = filtered.filter(coral => 
        coral.color && coral.color.toLowerCase().includes(colorFilter.toLowerCase())
      );
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'difficulty':
          const difficultyOrder = { 'Anfänger': 1, 'Mittel': 2, 'Fortgeschritten': 3 };
          return (difficultyOrder[a.difficulty_level] || 2) - (difficultyOrder[b.difficulty_level] || 2);
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'newest':
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });

    setFilteredCorals(filtered);
  }, [corals, searchTerm, difficultyFilter, colorFilter, sortBy]);

  useEffect(() => {
    fetchCorals();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    filterAndSortCorals();
  }, [filterAndSortCorals]);

  const handleContact = (coral) => {
    navigate('/kontakt', { state: { item: coral, type: 'Koralle' } });
  };

  const handleViewDetails = (coral) => {
    setSelectedProduct(coral);
  };

  const closeModal = () => {
    setSelectedProduct(null);
  };



  const clearFilters = () => {
    setSearchTerm('');
    setDifficultyFilter('');
    setColorFilter('');
    setSortBy('newest');
  };

  if (loading) {
    return (
      <>
        <PageHeader>
          <HeaderContent>
            <PageTitle>
              <GiCoral size={60} />
              Korallen
            </PageTitle>
            <PageDescription>
              Entdecken Sie unsere wunderschöne Auswahl an Korallen
            </PageDescription>
          </HeaderContent>
        </PageHeader>
        <ContentSection>
          <Container>
            <LoadingMessage>Korallen werden geladen...</LoadingMessage>
          </Container>
        </ContentSection>
      </>
    );
  }

  return (
    <>
      <PageHeader>
        <HeaderContent>
          <PageTitle>
            <GiCoral size={60} />
            Korallen
          </PageTitle>
          <PageDescription>
            Meine Korallen wachsen so schnell! Hier finde ich regelmässig neue Zuhause 
            für Ableger und zu gross gewordene Exemplare aus meinem Riffaquarium.
          </PageDescription>
        </HeaderContent>
      </PageHeader>

      <ContentSection>
        <Container>
          <FiltersSection>
            <FiltersGrid>
              <FilterGroup>
                <FilterLabel>Suchen</FilterLabel>
                <SearchContainer>
                  <SearchInput
                    type="text"
                    placeholder="Nach Korallen suchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <SearchIcon size={18} />
                </SearchContainer>
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Schwierigkeit</FilterLabel>
                <Select
                  value={difficultyFilter}
                  onChange={(e) => setDifficultyFilter(e.target.value)}
                >
                  <option value="">Alle Stufen</option>
                  <option value="Anfänger">Anfänger</option>
                  <option value="Mittel">Mittel</option>
                  <option value="Fortgeschritten">Fortgeschritten</option>
                </Select>
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Farbe</FilterLabel>
                <Select
                  value={colorFilter}
                  onChange={(e) => setColorFilter(e.target.value)}
                >
                  <option value="">Alle Farben</option>
                  <option value="Grün">Grün</option>
                  <option value="Blau">Blau</option>
                  <option value="Rot">Rot</option>
                  <option value="Orange">Orange</option>
                  <option value="Lila">Lila</option>
                  <option value="Gelb">Gelb</option>
                  <option value="Bunt">Bunt</option>
                </Select>
              </FilterGroup>

              <FilterGroup>
                <Button
                  outline
                  onClick={clearFilters}
                  style={{ 
                    height: '48px',
                    whiteSpace: 'nowrap',
                    minWidth: 'fit-content',
                    padding: '0 1rem'
                  }}
                >
                  <FiFilter size={16} />
                  Zurücksetzen
                </Button>
              </FilterGroup>
            </FiltersGrid>
          </FiltersSection>

          <ResultsHeader>
            <ResultsCount>
              {filteredCorals.length} Koralle{filteredCorals.length !== 1 ? 'n' : ''} gefunden
            </ResultsCount>
            <SortSection>
              <FilterLabel>Sortieren nach:</FilterLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{ minWidth: '150px' }}
              >
                <option value="newest">Neueste zuerst</option>
                <option value="oldest">Älteste zuerst</option>
                <option value="name">Name A-Z</option>
                <option value="difficulty">Schwierigkeit</option>
              </Select>
            </SortSection>
          </ResultsHeader>

          {filteredCorals.length === 0 ? (
            <EmptyState>
              <GiCoral className="empty-icon" />
              <div className="empty-title">
                {searchTerm || difficultyFilter || colorFilter
                  ? 'Keine Korallen gefunden'
                  : 'Noch keine Korallen verfügbar'
                }
              </div>
              <div className="empty-description">
                {searchTerm || difficultyFilter || colorFilter
                  ? 'Versuchen Sie es mit anderen Suchbegriffen oder Filtern.'
                  : 'Schauen Sie bald wieder vorbei oder kontaktieren Sie uns für spezielle Wünsche.'}
              </div>
              {(searchTerm || difficultyFilter || colorFilter) && (
                <Button onClick={clearFilters}>
                  Zurücksetzen
                </Button>
              )}
            </EmptyState>
          ) : (
            <ItemsGrid>
              {filteredCorals.map((coral) => (
                <ItemCard
                  key={coral.id}
                  item={coral}
                  type="coral"
                  onContact={handleContact}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </ItemsGrid>
          )}
        </Container>
      </ContentSection>
      
      {selectedProduct && (
        <ProductModal
          item={selectedProduct}
          type="coral"
          onClose={closeModal}
          onContact={handleContact}
        />
      )}
    </>
  );
};

export default Corals;

